import { Button, CssBaseline, CssFonts } from '@dbi-tools/ui-kit';
import { Banner, BannerKeys } from '@dbi-tools/ui-kit/lib/components/banner';
import { Page } from '@dbi-tools/ui-kit/lib/page';
import img403 from '@quiz-frontend/markup/images/error-403.svg';
import img404 from '@quiz-frontend/markup/images/error-404.svg';
import img500 from '@quiz-frontend/markup/images/error-500.svg';
import { PageError } from '@quiz-frontend/quiz-builder/store/app/types';
import { FunctionalPage } from '@quiz-frontend/quiz-builder/types/store-types';
import * as Sentry from '@sentry/node';
import NextError, { ErrorProps } from 'next/error';
import React, { Fragment } from 'react';

import Header from '../components/header';
import { APP_NAME } from '../types/app';

interface ErrorContent {
  code: number;
  title: string;
  text: string;
  img: { src: string };
}

const errors: ErrorContent[] = [
  {
    code: 400,
    title: 'Bad Request',
    text: 'Something went wrong. The server cannot process the request.',
    img: img500,
  },
  {
    code: 403,
    title: 'Forbidden',
    text: 'Access to this resource on the server is denied.',
    img: img403,
  },
  {
    code: 404,
    title: 'Page not found',
    text: 'Sorry, the page you was trying to view does not exist.',
    img: img404,
  },
  {
    code: 500,
    title: 'Internal server error',
    text: 'Something went wrong with our servers.\nDon’t worry our development team has automatically been notified of the issue and they are working on it.\nPlease try again in a few minutes.',
    img: img500,
  },
];
const defaultError = errors.find((item) => item.code === 500);

const ErrorPage: FunctionalPage<{
  statusCode: number;
  title?: string;
  skipErrorLog: boolean;
  err: PageError | Error;
  appName: APP_NAME;
}> = ({ statusCode, skipErrorLog, err, appName }) => {
  if (!skipErrorLog && err) {
    // getInitialProps is not called in case of
    // https://github.com/zeit/next.js/issues/8592. As a workaround, we pass
    // err via _app.js so it can be captured
    Sentry.captureException(err);
  }

  const content = errors.find((item) => item.code === statusCode) || defaultError;
  const isVisionBoard = appName === APP_NAME.VisionBoard;

  return (
    <Fragment>
      <CssBaseline />
      <CssFonts />
      <Page bg="secondaryLight">
        <Banner active={isVisionBoard ? BannerKeys.PEARL : BannerKeys.DAVIDS} />
        <Header {...{ appName }} />
        <Page.Error
          img={content.img.src}
          code={content.code}
          title={content.title}
          text={content.text}
          link={
            <Button variant="solid" color="primaryDark" as="a" href="/" fullWidth>
              Go to the homepage
            </Button>
          }
          style={{ paddingTop: '40px' }}
        />
      </Page>
    </Fragment>
  );
};

ErrorPage.getInitialProps = async (props) => {
  const { res, err, asPath } = props;
  let statusCode: number;
  if (res) {
    statusCode = res.statusCode;
  } else {
    statusCode = err ? err.statusCode : 404;
  }

  const errorInitialProps = (await NextError.getInitialProps(props)) as ErrorProps & { skipErrorLog: boolean };

  // Workaround for https://github.com/zeit/next.js/issues/8592, mark when
  // getInitialProps has run
  errorInitialProps.statusCode = statusCode;
  errorInitialProps.skipErrorLog = true;

  if (res) {
    // Running on the server, the response object is available.
    //
    // Next.js will pass an err on the server if a page's `getInitialProps`
    // threw or returned a Promise that rejected

    if (res.statusCode === 404) {
      // Opinionated: do not record an exception in Sentry for 404
      return { statusCode: 404 };
    }

    if (err) {
      Sentry.captureException(err);

      return errorInitialProps;
    }
  } else {
    // Running on the client (browser).
    //
    // Next.js will provide an err if:
    //
    //  - a page's `getInitialProps` threw or returned a Promise that rejected
    //  - an exception was thrown somewhere in the React lifecycle (render,
    //    componentDidMount, etc) that was caught by Next.js's React Error
    //    Boundary. Read more about what types of exceptions are caught by Error
    //    Boundaries: https://reactjs.org/docs/error-boundaries.html
    if (err) {
      Sentry.captureException(err);

      return errorInitialProps;
    }
  }

  // If this point is reached, getInitialProps was called without any
  // information about what the error might be. This is unexpected and may
  // indicate a bug introduced in Next.js, so record it in Sentry
  Sentry.captureException(new Error(`_error.js getInitialProps missing data at path: ${asPath}`));

  return errorInitialProps;
};

export default ErrorPage;
